<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { Motion } from '@motionone/vue'
import { Button } from '@/components/ui/button'
import { PathsCanvas } from '.'

interface Props {
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Background Paths',
})

const words = computed(() => props.title.split(' '))
const mounted = ref(false)
onMounted(() => (mounted.value = true))

interface PathDef {
  id: number
  d: string
  width: number
  opacity: number
  duration: number
}

function makePaths(position: number): PathDef[] {
  return Array.from({ length: 36 }, (_, i) => ({
    id: i,
    d: `M-${380 - i * 5 * position} -${189 + i * 6}C-${380 - i * 5 * position} -${189 + i * 6} -${312 - i * 5 * position} ${216 - i * 6} ${152 - i * 5 * position} ${343 - i * 6}C${616 - i * 5 * position} ${470 - i * 6} ${684 - i * 5 * position} ${875 - i * 6} ${684 - i * 5 * position} ${875 - i * 6}`,
    width: 0.5 + i * 0.03,
    opacity: Math.min(0.55, 0.12 + i * 0.02),
    duration: 18 + (i % 10),
  }))
}

// Background now uses CSS-animated PathsCanvas for better perf and look
</script>

<template>
  <div class="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-white dark:bg-neutral-950">
    <!-- Background animated paths -->
    <ClientOnly>
      <PathsCanvas class="text-slate-950 dark:text-white" :count="24" :segments-per-path="3" :baton-length-ratio="0.22" />
    </ClientOnly>

    <!-- Foreground content -->
    <div class="relative z-10 container mx-auto px-4 md:px-6 text-center">
      <div class="max-w-4xl mx-auto">
        <h1 class="text-5xl sm:text-7xl md:text-8xl font-bold mb-8 tracking-tighter">
          <template v-if="mounted">
            <span v-for="(word, wordIndex) in words" :key="wordIndex" class="inline-block mr-4 last:mr-0">
              <Motion
                v-for="(letter, letterIndex) in word.split('')"
                :key="`${wordIndex}-${letterIndex}`"
                tag="span"
                :initial="{ y: 100, opacity: 0 }"
                :animate="{ y: 0, opacity: 1 }"
                :transition="{ delay: wordIndex * 0.1 + letterIndex * 0.03, type: 'spring', stiffness: 150, damping: 25 }"
                class="inline-block text-transparent bg-clip-text bg-gradient-to-r from-neutral-900 to-neutral-700/80 dark:from-white dark:to-white/80"
              >
                {{ letter }}
              </Motion>
            </span>
          </template>
        </h1>

        <div
          class="inline-block group relative bg-gradient-to-b from-black/10 to-white/10 dark:from-white/10 dark:to-black/10 p-px rounded-2xl backdrop-blur-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
        >
          <Button
            variant="ghost"
            class="rounded-[1.15rem] px-8 py-6 text-lg font-semibold backdrop-blur-md bg-white/95 hover:bg-white/100 dark:bg-black/95 dark:hover:bg-black/100 text-black dark:text-white transition-all duration-300 group-hover:-translate-y-0.5 border border-black/10 dark:border-white/10 hover:shadow-md dark:hover:shadow-neutral-800/50"
          >
            <span class="opacity-90 group-hover:opacity-100 transition-opacity">Discover Excellence</span>
            <span class="ml-3 opacity-70 group-hover:opacity-100 group-hover:translate-x-1.5 transition-all duration-300">→</span>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
